import { endPeonHandoff, silencePeon } from "./audio";
import { report } from "./telemetry";

/**
 * Bookkeeping around handing off to the camera or photo library.
 *
 * On Android the camera is a separate app, and the system can kill ours in
 * the background while it is open; the photo then has nowhere to go and the
 * app restarts. Before the hand-off the peon is silenced (no voice line or
 * speaking animation running as the camera opens), and the start is written
 * to localStorage (it survives the process being killed). A file arriving,
 * or the picker being cancelled, clears it. If the app starts again with the
 * entry still there, the photo was lost to a restart, and that is reported
 * as photo.lost so it can be counted in the workshop's Device log.
 */

const KEY = "op.photoPick";
/** A pick older than this when the app starts is stale, not a lost photo. */
const LOST_WINDOW_MS = 5 * 60_000;
/** Set when this launch picked up a photo from before the restart (markPhotoPickRestored). */
let restoredThisLaunch = false;

/** Where the photo comes from: the camera, the library, or the phone's own chooser ("either", Job's Done). */
export type PhotoSource = "camera" | "library" | "either";

/**
 * `purpose` names what the photo is for when two pickers share a route (a project's Add progress beside
 * Job's Done, shared/projects.ts PROGRESS_PICK): a photo restored after a restart goes only to the picker
 * with the same purpose. Unset for every other picker.
 */
export function beginPhotoPick(kind: PhotoSource, purpose = ""): void {
  try {
    silencePeon();
  } catch {
    /* never block the picker */
  }
  try {
    localStorage.setItem(KEY, JSON.stringify({ at: Date.now(), kind, route: location.hash, ...(purpose ? { purpose } : {}) }));
  } catch {
    /* storage unavailable */
  }
}

/**
 * Records how far the photo got, in the same surviving entry: the file arriving from the camera,
 * the resize finishing, the check screen showing, the confirm tap, the upload finishing. A restart
 * reports the last stage reached, which is what tells a kill during the camera from a crash while
 * the photo is being processed.
 */
export type PhotoStage = "file" | "prepared" | "check" | "confirmed" | "uploaded";
export function markPhotoStage(stage: PhotoStage): void {
  if (stage === "file") endPeonHandoff();
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return;
    const entry = JSON.parse(raw) as Record<string, unknown>;
    entry.stage = stage;
    entry.stageAt = Date.now();
    localStorage.setItem(KEY, JSON.stringify(entry));
  } catch {
    /* ignore */
  }
}

export function endPhotoPick(): void {
  endPeonHandoff();
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}

export interface PhotoPickEntry {
  at: number;
  kind: PhotoSource;
  route: string;
  /** What the photo is for, when its picker said (beginPhotoPick); "" for any other. */
  purpose?: string;
  stage?: PhotoStage;
  stageAt?: number;
}

/**
 * The pick still waiting for its photo, if it started within the lost-photo window. In the Android
 * app (src/native-camera.ts) a camera result that Android re-delivers after killing the app is
 * matched to this entry at startup, before reportLostPhotoPick runs.
 */
export function pendingPhotoPick(): PhotoPickEntry | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const entry = JSON.parse(raw) as Partial<PhotoPickEntry>;
    const age = Date.now() - (entry.at ?? 0);
    if (age < 0 || age >= LOST_WINDOW_MS) return null;
    return { at: entry.at ?? 0, kind: entry.kind === "library" || entry.kind === "either" ? entry.kind : "camera", route: entry.route ?? "", ...(typeof entry.purpose === "string" && entry.purpose ? { purpose: entry.purpose } : {}), stage: entry.stage, stageAt: entry.stageAt };
  } catch {
    return null;
  }
}

/**
 * The photo of a pick made before a restart came back after all (the Android app's Camera plugin
 * re-delivers it): the entry is marked with the file stage, so reportLostPhotoPick stays quiet and
 * the rest of the flow (prepared, check, uploaded) carries on in a fresh entry, and the Device log
 * gets a photo.restored row instead of photo.lost.
 */
export function markPhotoPickRestored(entry: PhotoPickEntry): void {
  restoredThisLaunch = true;
  try {
    localStorage.setItem(KEY, JSON.stringify({ at: Date.now(), kind: entry.kind, route: entry.route, ...(entry.purpose ? { purpose: entry.purpose } : {}), stage: "file", stageAt: Date.now() }));
  } catch {
    /* ignore */
  }
  report("photo.restored", entry.stage ?? "camera-open", { kind: entry.kind, ageMs: Date.now() - entry.at, route: entry.route });
}

/** Called once at startup: reports a pick that never came back. */
export function reportLostPhotoPick(): void {
  // A pick restored at this launch is still in flight (its check is about to show), so its entry
  // stays for the remaining stages; a restart after this one reports it as usual.
  if (restoredThisLaunch) return;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return;
    localStorage.removeItem(KEY);
    const entry = JSON.parse(raw) as { at?: number; kind?: string; route?: string; stage?: string; stageAt?: number };
    const age = Date.now() - (entry.at ?? 0);
    if (age < 0 || age >= LOST_WINDOW_MS) return;
    const nav = performance.getEntriesByType("navigation")[0] as PerformanceNavigationTiming | undefined;
    report("photo.lost", entry.stage ?? "camera-open", {
      kind: entry.kind ?? "",
      ageMs: age,
      sinceStageMs: entry.stageAt ? Date.now() - entry.stageAt : null,
      route: entry.route ?? "",
      navigation: nav?.type ?? "",
      deviceMemory: (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? null,
    });
  } catch {
    /* ignore */
  }
}
